import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { arrayMove } from '@dnd-kit/sortable'

import { useFetch } from '@reveni-io/reveni-ui'

import useFetchPolicyRules from '@/common/hooks/useFetchPolicyRules/useFetchPolicyRules'
import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

import { pageSize, policyRuleStates } from './constants'
import { getStateParam } from './helpers'

const usePolicyRules = ({ storeId, setToastInfo }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { policyRulesData, fetchPolicyRules, gettingPolicyRules } = useFetchPolicyRules()

  const { fetchData: deletePolicyRuleRequest, loading: deletingPolicyRule } = useFetch(
    configurationServices.deletePolicyRule,
    { doInitialRequest: false }
  )

  const { fetchData: reorderPolicyRulesRequest, loading: reorderingPolicyRules } = useFetch(
    configurationServices.reorderPolicyRules,
    { doInitialRequest: false }
  )

  const [policyRules, setPolicyRules] = useState([])
  const [hasChanges, setHasChanges] = useState(false)
  const [search, setSearch] = useState('')
  const [ruleToDelete, setRuleToDelete] = useState(null)
  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)
  const [summary, setSummary] = useState(null)
  const [selectedState, setSelectedState] = useState(policyRuleStates.current)
  const pageRef = useRef(1)

  useEffect(() => {
    setPage(1)
    pageRef.current = 1
  }, [search, selectedState])

  useEffect(() => {
    pageRef.current = page
    fetchPolicyRules({ storeId, search, page, pageSize, state: getStateParam(selectedState) })
  }, [storeId, search, page, selectedState, fetchPolicyRules])

  const setInitValues = useCallback(() => {
    if (policyRulesData) {
      if (pageRef.current === 1) {
        setPolicyRules(policyRulesData.results)
      } else {
        setPolicyRules(prev => [...prev, ...policyRulesData.results])
      }
      setCount(policyRulesData.count)
      setSummary(policyRulesData.summary)
    }
    setHasChanges(false)
  }, [policyRulesData])

  useEffect(() => {
    setInitValues()
  }, [setInitValues])

  const reorderRules = event => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    setPolicyRules(prev => {
      const oldIndex = prev.findIndex(r => r.id === active.id)
      const newIndex = prev.findIndex(r => r.id === over.id)
      return arrayMove(prev, oldIndex, newIndex)
    })
    setHasChanges(true)
  }

  const refetchFromStart = useCallback(() => {
    setPage(1)
    pageRef.current = 1
    fetchPolicyRules({ storeId, search, page: 1, pageSize, state: getStateParam(selectedState) })
  }, [storeId, search, selectedState, fetchPolicyRules])

  const handleShowMore = () => {
    setPage(prev => prev + 1)
  }

  const onSave = async () => {
    const rulesPayload = policyRules.map((rule, index) => ({
      id: rule.id,
      enabled: rule.is_enabled,
      position: index,
    }))

    try {
      await reorderPolicyRulesRequest({ storeId, rules: rulesPayload })
      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
      refetchFromStart()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const revertChanges = () => {
    setInitValues()
  }

  const handleChangeEnabled = (rule, checked) => {
    setPolicyRules(prev =>
      prev.map(r => {
        if (r.id === rule.id) {
          return { ...r, is_enabled: checked }
        }
        return r
      })
    )
    setHasChanges(true)
  }

  const confirmDeleteRule = async () => {
    if (!ruleToDelete) return

    try {
      await deletePolicyRuleRequest({ storeId, ruleId: ruleToDelete.id })
      setToastInfo({ show: true, message: t('Eliminado correctamente'), type: 'success' })
      setRuleToDelete(null)
      refetchFromStart()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const cancelDeleteRule = () => {
    setRuleToDelete(null)
  }

  const navigateToDetail = rule => {
    navigate(routes.configurationPage.returnPolicy.policyRuleDetail.replace(':id', rule.id), {
      state: { childPageTitle: rule.name, selectedRule: rule },
    })
  }

  const navigateToDuplicate = rule => {
    navigate(routes.configurationPage.returnPolicy.policyRuleDetail.replace(':id', `duplicate-${rule.id}`), {
      state: { selectedRule: rule },
    })
  }

  const navigateToCreate = () => {
    navigate(routes.configurationPage.returnPolicy.policyRuleDetail.replace(':id', 'new'))
  }

  const isEmpty = !gettingPolicyRules && summary?.all === 0 && !search
  const isSelectedTabEmpty = !gettingPolicyRules && summary?.all > 0 && summary?.[selectedState] === 0

  return {
    policyRules,
    setPolicyRules: params => {
      setPolicyRules(params)
      setHasChanges(true)
    },
    gettingPolicyRules,
    deletingPolicyRule,
    reorderingPolicyRules,
    hasChanges,
    search,
    setSearch,
    page,
    count,
    pageSize,
    summary,
    selectedState,
    setSelectedState,
    isEmpty,
    isSelectedTabEmpty,
    handleShowMore,
    ruleToDelete,
    setRuleToDelete,
    reorderRules,
    onSave,
    revertChanges,
    handleChangeEnabled,
    confirmDeleteRule,
    cancelDeleteRule,
    navigateToDetail,
    navigateToDuplicate,
    navigateToCreate,
  }
}

export default usePolicyRules
